import { getWebPath } from '@doss/common-all'
import dayjs from 'dayjs'
import { MouseEventHandler, useCallback } from 'react'
import { PiArchive, PiCheckSquare, PiNotification } from 'react-icons/pi'
import { TbMessageCircle } from 'react-icons/tb'
import { useNavigate } from 'react-router'
import {
  NotificationType,
  useMarkNotificationAsArchivedMutation,
  useMarkNotificationAsReadMutation,
  useMarkNotificationAsUnreadMutation,
} from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { apolloClient } from '../../common/apollo-client'
import { Notification } from '../../common/hooks/types'
import { DossSignalType, useDossSignals } from '../../common/hooks/useDossSignals'
import { CommentContent } from '../activity/comment/CommentContent'
import Link from '../shared/Link'
import { Tooltip } from '../ui/atoms/tooltip/Tooltip'
import { UserAvatar } from '../users/UserAvatar'

interface CommentNotificationProps {
  notification: Notification
}

/**
 * Renders a comment notification
 */
export function CommentNotification({ notification }: CommentNotificationProps) {
  const navigate = useNavigate()
  const { dispatch } = useDossSignals()
  const { t } = useTypedTranslation()

  const [markNotificationAsRead, { loading: markingAsRead }] = useMarkNotificationAsReadMutation({
    onCompleted: () => {
      apolloClient.cache.evict({ fieldName: 'paginatedNotifications' })
      apolloClient.cache.gc()
    },
  })

  const [markNotificationAsUnread, { loading: markingAsUnread }] =
    useMarkNotificationAsUnreadMutation({
      onCompleted: () => {
        apolloClient.cache.evict({ fieldName: 'paginatedNotifications' })
        apolloClient.cache.gc()
      },
    })

  const [markNotificationAsArchived, { loading: markingAsArchived }] =
    useMarkNotificationAsArchivedMutation({
      onCompleted: () => {
        apolloClient.cache.evict({ fieldName: 'paginatedNotifications' })
        apolloClient.cache.gc()
      },
    })

  const handleToggleRead = useCallback<MouseEventHandler<HTMLDivElement>>(
    (e) => {
      e.stopPropagation()
      if (markingAsRead || markingAsUnread) {
        return
      }
      if (notification.isRead) {
        markNotificationAsUnread({ variables: { notificationId: notification.id } })
      } else {
        markNotificationAsRead({ variables: { notificationId: notification.id } })
      }
    },
    [
      notification.id,
      notification.isRead,
      markNotificationAsRead,
      markNotificationAsUnread,
      markingAsRead,
      markingAsUnread,
    ],
  )

  const handleArchival = useCallback<MouseEventHandler<HTMLDivElement>>(
    (e) => {
      e.stopPropagation()
      if (markingAsArchived) {
        return
      }
      markNotificationAsArchived({ variables: { notificationId: notification.id } })
    },
    [notification.id, markNotificationAsArchived, markingAsArchived],
  )

  const handleClick = useCallback<MouseEventHandler<HTMLDivElement>>(() => {
    const comment = notification.comment
    if (!comment) {
      return
    }

    if (!notification.isRead && !markingAsRead) {
      markNotificationAsRead({ variables: { notificationId: notification.id } })
    }

    const parentObject = comment.workspace.parentObject
    if (!parentObject) {
      // Stay on the panel: user may open another notification or archive; no destination to navigate to.
      return
    }

    navigate(
      getWebPath({
        ...parentObject,
        commentId: comment.id,
      }),
    )
    dispatch(DossSignalType.CLOSE_NOTIFICATIONS_PANEL)
  }, [markNotificationAsRead, navigate, notification, markingAsRead, dispatch])

  // Comment notifications must have a comment
  if (!notification.comment) {
    return null
  }

  const { comment } = notification
  const parentObject = comment.workspace.parentObject

  let notificationReason = ''
  switch (notification.notificationType) {
    case NotificationType.MENTION:
      notificationReason = t('comment_notification.mentioned')
      break
    case NotificationType.THREAD_REPLY:
      notificationReason = t('comment_notification.replied')
      break
    case NotificationType.ALERT:
      notificationReason = t('comment_notification.alert')
      break
    case NotificationType.ACTION_STEP_NOTIFICATION:
      // This case should never happen as ACTION_STEP_NOTIFICATION is handled by ActionStepNotification component
      console.error('ACTION_STEP_NOTIFICATION should not be rendered in CommentNotification')
      return null
  }

  return (
    <div
      className="group relative flex cursor-pointer items-start border-b border-gray-4 px-3 pb-4 pt-5 hover:bg-gray-1"
      onClick={handleClick}
    >
      <div className="absolute end-5 top-3 hidden items-center gap-0.5 rounded bg-white p-0.5 shadow group-hover:flex">
        <Tooltip enterDelay={20} title={notification.isRead ? 'Mark as unread' : 'Mark as read'}>
          <div className="cursor-pointer rounded p-1 hover:bg-gray-1" onClick={handleToggleRead}>
            {notification.isRead ? <PiNotification size={16} /> : <PiCheckSquare size={16} />}
          </div>
        </Tooltip>
        <Tooltip enterDelay={20} title="Remove notification">
          <div className="cursor-pointer rounded p-1 hover:bg-gray-1" onClick={handleArchival}>
            <PiArchive size={16} />
          </div>
        </Tooltip>
      </div>
      <div className="pt-2">
        <div
          className={`size-2 rounded-[50%] bg-blue-8 ${notification.isRead ? 'invisible' : 'visible'}`}
        ></div>
      </div>
      <div className="flex flex-col px-3">
        <div className="flex items-center gap-2">
          <UserAvatar className="border" small user={{ ...comment.author, email: '' }} />
          <div className="text-xs font-normal">
            {comment.author.firstName} {notificationReason}
          </div>
        </div>
        <div className="flex items-center gap-2 ps-8 text-gray-8">
          <div>{dayjs(comment.createdAt).fromNow()}</div>
          <div className="size-1 rounded-[50%] bg-gray-8"></div>
          {parentObject ? (
            <Link
              className="group/link flex items-center gap-2 text-gray-8 hover:text-gray-9"
              href={getWebPath(parentObject)}
              noLinkStyle
            >
              <TbMessageCircle />
              <div className="group-hover/link:underline">{parentObject.name}</div>
            </Link>
          ) : (
            <span className="flex items-center gap-2">
              <TbMessageCircle />
              <span>{t('common.unknown')}</span>
            </span>
          )}
        </div>
        <div className="flex ps-6 pt-1 text-xs">
          <div className="rounded px-2 py-1 hover:bg-gray-2">
            <CommentContent comment={comment} />
          </div>
        </div>
      </div>
    </div>
  )
}
